<template>
    <div
        class="resources-hero section-wrapper"
        :class="viewport.is"
        @vue:mounted="setData"
    >
        <section>
            <GradientText
                tag="h1"
                :size="viewport.is.mobile ? 'l' : 'xxl'" heading
            >
                Resources
            </GradientText>
            <Txt :size="viewport.is.mobile ? 'xl' : 'xxl'">
                The latest industry trends, inspiring client stories & course building guides.
            </Txt>
        </section>
    </div>
    <div
        v-if="featured"
        class="resources-featured section-wrapper"
        :class="viewport.is"
    >
        <section>
            <Chip
                icon="star"
                variant="smoke"
                :text="{ size: 'xxxs', heading: true }"
            >
                Featured
            </Chip>
            <ResourceCard
                :resource="featured"
                featured
            />
        </section>
    </div>
    <div
        class="resources section-wrapper"
        :class="viewport.is"
    >
        <Datamodel
            ref="dm"
            :data-prop="_resources"
        >
            <template #default="dm">
                <section>
                    <Txt v-if="!viewport.is.xl" size="xxs" heading>
                        Filter by
                    </Txt>
                    <aside>
                        <section class="filters">
                            <Button
                                variant="ghost"
                                colour="steel"
                                hover="white"
                                size="xxs" heading
                                slim
                                :active="!featuredFilter"
                                @click="featuredFilter = !featuredFilter"
                            >
                                {{ viewport.is.xl ? 'Latest posts' : 'Latest' }}
                            </Button>
                            <Button
                                variant="ghost"
                                colour="steel"
                                hover="white"
                                size="xxs" heading
                                slim
                                :active="featuredFilter"
                                @click="featuredFilter = !featuredFilter"
                            >
                                {{ viewport.is.xl ? 'Featured posts' : 'Featured' }}
                            </Button>
                        </section>
                        <template
                            v-for="(filter, key) in filters"
                            :key="key"
                        >
                            <Txt v-if="viewport.is.xl" size="xxs" heading>
                                {{ filter.title }}
                            </Txt>
                            <section class="filters">
                                <Button
                                    v-for="option in filter.options"
                                    :key="option.title"
                                    variant="ghost"
                                    colour="steel"
                                    hover="white"
                                    size="xxs" heading
                                    slim
                                    :active="option.title === filter.selected"
                                    @click="setFilter(dm, filter, option.title)"
                                >
                                    {{ option.title }}
                                </Button>
                            </section>
                        </template>
                        <SubscribeForm v-if="viewport.is.xl" />
                    </aside>
                    <div class="resources-grid">
                        <ResourceCard
                            v-for="resource in dm.currentData"
                            :key="resource.id"
                            :resource="resource"
                        />
                        <Button
                            v-if="!allLoaded"
                            variant="create"
                            size="xxs" heading
                            :status="status"
                            @click="getResources"
                        >
                            Load more
                        </Button>
                    </div>
                </section>
            </template>
        </Datamodel>
    </div>
</template>

<script>
definePageMeta({
    layout: 'cms',
});

import { defineAsyncComponent } from 'vue';
import { useResponsive } from '@/store/responsive';
import { fetchEntries } from '@builder.io/sdk-vue';

import Txt from '@/components/form/txt';
import Chip from '@/components/utility/chip';
import Button from '@/components/utility/button';
import GradientText from '@/components/animation/gradient-text';
import ResourceCard from '@/components/author/cms/resource-card';

const Datamodel = defineAsyncComponent(() => import('@/components/data/datamodel'));
const SubscribeForm = defineAsyncComponent(() => import('@/components/author/cms/subscribe-form'));

export default {
    name: 'Resources',
    components: { Txt, Chip, Button, Datamodel, GradientText, ResourceCard, SubscribeForm },
    inject: ['$global', 'resources', 'categories', 'topics', 'contentTypes'],
    data() {
        return {
            _resources: null,

            filters: {
                categories: {
                    title: 'Categories',
                    by: 'category',
                    selected: null,
                    options: []
                },
                topics: {
                    title: 'Topics',
                    by: 'topic',
                    selected: null,
                    options: []
                },
                contentTypes: {
                    title: 'Content types',
                    by: 'contentType',
                    selected: null,
                    options: []
                },
            },
            featuredFilter: false,
            status: null,
            allLoaded: false,

            viewport: { is: {} }
        };
    },
    computed: {
        filterTypes() {
            return {
                Categories: this.categories,
                Topics: this.topics,
                'Content types': this.contentTypes
            };
        },
        featured() {
            if (!this._resources) return;
            return this._resources.find(resource => resource.featured);
        }
    },
    watch: {
        featuredFilter(to) {
            if (to) {
                this.$refs.dm.setFilter({ by: 'featured' }, to);
            } else {
                this.$refs.dm.clearFilterValue({ by: 'featured' });
            }
        }
    },
    mounted() {
        this.viewport = useResponsive();
    },
    methods: {
        setData() {
            this._resources = this.resources;
            this.filters.categories.options = this.categories;
            this.filters.topics.options = this.topics;
            this.filters.contentTypes.options = this.contentTypes;
            this.allLoaded = this.resources.length < 10;
        },
        getResources() {
            if (this.status) return;
            this.status = 'waiting';
            fetchEntries({
                model: 'resource',
                apiKey: this.apiKey,
                options: {
                    omit: 'data.blocks',
                    includeUnpublished: this.$global.isProduction === false
                },
                limit: 10,
                offset: this._resources && this._resources.length || 0,
            }).then(response => {
                const resources = response.map(template => template.data);
                this._resources.push(...resources);
                this.status = null;
                this.allLoaded = resources.length < 10;
            });
        },
        setFilter(dm, filter, option) {
            if (filter.selected === option) {
                filter.selected = null;
            } else {
                filter.selected = option;
            }
            dm.setFilter(filter, option);
        },
    }
};
</script>

<style lang="less" scoped>
.resources-hero.section-wrapper {
    padding-bottom: 0;
    section {
        text-align: center;
        align-items: center;
        gap: 16px;
        color: var(--navy);
    }
    &.mobile {
        padding: 48px 24px;
        padding-bottom: 0;
    }
}
.resources-featured.section-wrapper {
    position: relative;
    overflow: visible;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0) 40%, var(--cloud) 40%, var(--cloud) 100%);
    section {
        align-items: flex-start;
        gap: 8px;
        .chip { --chip-icon-colour: var(--primary); }
    }
    &::before {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        width: 680px;
        height: 680px;
        transform: rotate(100deg);
        z-index: -1;
        background: radial-gradient(50% 50% at 50% 50%, #98D6E4 0%, rgba(255, 255, 255, 0.00) 100%);
    }
    &.mobile {
        padding: 48px 24px;
        padding-bottom: 0;
        &::before {
            display: none;
        }
    }
}
.resources.section-wrapper {
    padding: 0 64px 64px 64px;
    background-color: var(--cloud);
    section {
        align-items: flex-start;
        flex-direction: column;
        color: var(--navy);
        gap: 24px;
        aside {
            display: flex;
            flex-direction: row;
            align-items: flex-start;
            gap: 8px;
            overflow: auto;
            margin: 0 -24px;
            padding: 0 24px 8px 24px;
            width: calc(100% + 48px);
            section.filters {
                display: flex;
                flex-direction: row;
                gap: 8px;
                .cobutton.ghost {
                    padding: 8px 12px;
                    border-radius: 24px;
                }
            }
        }
        .resources-grid {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 24px;
            color: var(--coal);
            align-items: center;
            > .cobutton { 
                width: 180px;
                margin-top: 16px;
            }
        }
    }
    &.xl {
        section {
            gap: 64px;
            flex-direction: row;
            aside {
                flex-direction: column;
                position: sticky;
                top: 0;
                flex: 0 0 336px;
                gap: 24px;
                section.filters {
                    flex-wrap: wrap;
                    flex-direction: column;
                }
            }
        }
    }
    &.mobile {
        padding: 24px;
    }
}
</style>

<style lang="less">
.resources.section-wrapper {
    section {
        aside section.filters {
            .cobutton .txt-component.heading { font-weight: 500; }
        }
    }
}
</style>
